import React, { useContext, useLayoutEffect, useRef } from 'react';
import { Trans } from 'react-i18next';
import { ChannelsContext } from 'lane-shared/contexts';
import { Icon } from 'design-system-web';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import styles from './HomeChannelAssociationBanner.scss';

const BANNER_HEIGHT_VAR = '--home-channel-association-banner-height';

export default function HomeChannelAssociationBanner() {
  const {
    homeChannelAssociationData,
    isHomeChannelAssociationBannerDismissed,
    dismissHomeChannelAssociationBanner,
  } = useContext(ChannelsContext);

  const bannerRef = useRef<HTMLDivElement>(null);

  const isHomeChannelSelectionEnabled = useFlag(
    FeatureFlag.UserHomeChannelSelection,
    false,
  );

  const isVisible =
    isHomeChannelSelectionEnabled &&
    Boolean(homeChannelAssociationData) &&
    !isHomeChannelAssociationBannerDismissed;

  useLayoutEffect(() => {
    const height =
      isVisible && bannerRef.current
        ? `${bannerRef.current.offsetHeight}px`
        : '0px';

    document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, height);

    return () => {
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, '0px');
    };
  }, [isVisible]);

  if (!isVisible) {
    return null;
  }

  return (
    <div
      ref={bannerRef}
      className={styles.banner}
      data-testid="home-channel-association-banner"
    >
      <div className={styles.content}>
        <Icon name="exclamation-triangle" className={styles.icon} />

        <div className={styles.message}>
          <Trans
            i18nKey="web.components.homeChannelAssociationBanner.message"
            values={{
              previousChannelName:
                homeChannelAssociationData?.previousChannelName ?? '',
              currentChannelName:
                homeChannelAssociationData?.currentChannelName ?? '',
            }}
            components={{ bold: <strong /> }}
          />
        </div>

        <button
          type="button"
          className={styles.closeButton}
          aria-label="Close banner"
          data-testid="home-channel-association-banner-close"
          onClick={dismissHomeChannelAssociationBanner}
        >
          <Icon name="times" className={styles.closeIcon} />
        </button>
      </div>
    </div>
  );
}
